// Line Entry — the quick-add bar (canvas board 6): scan or type a code.
//
//   ITM-014      adds 1         12*ITM-014   adds 12       -1*ITM-014  takes 1 off
//
// The engine resolves the code against the form's `quick_add` source
// (`match_lines`, exact on its match columns — item code, barcode) and builds
// the line through the form's own rules. A code already on a line adds to its
// quantity (`existing: "increment"`, written through the grid's own cell
// write so its item_change runs) or opens a new line. A session log keeps
// every scan, with "Undo last".

import { forwardRef, useCallback, useMemo, useRef, useState } from 'react'
import { Check, Plus, ScanLine, X } from 'lucide-react'
import type { LineExecute, LineRowResult } from './contract'
import { toBufferRow } from './contract'
import { evaluateLineLimits, type NormalizedLineEntry, type ResolvedLineLimit } from './model'
import type { ColumnDef, LineSource } from '../types'
import { columnFormKey } from '../utils'
import { T, btnSm, kbd } from './styles'

export interface ScanEntry {
  id: number
  at: number
  kind: 'added' | 'new' | 'missing' | 'error'
  code: string
  qty: number
  title: string
  detail: string
  /** How to take this scan back. */
  undo?: { kind: 'set'; rowKey: string; col: string; value: unknown } | { kind: 'remove'; rowKey: string }
}

export interface QuickAddDeps {
  execute: LineExecute
  formNo: string
  headerValues: Record<string, unknown>
  txnId?: string
  parentPk?: Record<string, unknown>
  rows: Array<Record<string, unknown>>
  rowKeyOf: (row: Record<string, unknown>) => string
  /** Append engine-built lines (each gets a fresh client id). Returns their keys. */
  onAppend: (rows: Array<Record<string, unknown>>) => string[]
  /** Write one cell through the grid's own path (item_change runs). */
  onSetCell: (rowKey: string, col: string, value: unknown) => void
  onRemoveRow: (rowKey: string) => void
  labelOf: (col: string) => string
}

/** The line columns a scan matches an existing line on (the source's link,
 *  else its mapped columns, else the lookup column) and the column it adds
 *  to (`quick_add.quantity`, else the source's status column, else its first
 *  editable picker column) — the client twin of the engine's `match_lines`. */
export function quickAddBinding(
  le: NormalizedLineEntry | null | undefined, cols: ColumnDef[],
): { keys: string[]; qty: string | null; source: LineSource | null } {
  const qa = le?.quickAdd
  const src = qa ? le?.sourcesByName[qa.source] || null : null
  if (!src) return { keys: [], qty: null, source: null }
  const byName = (n: unknown) => cols.find(c => String(c.name).toLowerCase() === String(n).toLowerCase())
  const names = [...(src.link?.to || []), ...(src.map || []).map(m => m.to),
    ...(src.from.lookup_of ? [src.from.lookup_of] : [])]
  const keys = Array.from(new Set(names.map(byName).filter((c): c is ColumnDef => !!c).map(columnFormKey)))
  const qname = qa?.quantity || Object.entries(src.limits || {}).find(([, l]) => l?.status)?.[0]
    || src.picker?.edit?.[0]
  const qc = qname ? byName(qname) : undefined
  return { keys, qty: qc ? columnFormKey(qc) : null, source: src }
}

/** "12*ITM-014" → {qty: 12, code: "ITM-014"}; "ITM-014" → qty 1. */
export function parseScan(text: string): { qty: number; code: string } | null {
  const t = String(text || '').trim()
  if (!t) return null
  const m = /^(-?\d+(?:\.\d+)?)\s*[*x×]\s*(.+)$/i.exec(t)
  if (m) return { qty: Number(m[1]), code: m[2].trim() }
  return { qty: 1, code: t }
}

let seq = 0

export function useQuickAdd(d: QuickAddDeps) {
  const ref = useRef(d)
  ref.current = d
  const [log, setLog] = useState<ScanEntry[]>([])
  const [busy, setBusy] = useState(false)

  const push = (e: Omit<ScanEntry, 'id' | 'at'>) => {
    seq += 1
    setLog(prev => [{ ...e, id: seq, at: Date.now() }, ...prev].slice(0, 200))
  }

  const lineNo = (rowKey: string) => {
    const live = ref.current.rows.filter(r => !r._deleted)
    const i = live.findIndex(r => ref.current.rowKeyOf(r) === rowKey)
    return i >= 0 ? i + 1 : null
  }

  const scan = useCallback(async (text: string) => {
    const p = parseScan(text)
    if (!p) return
    const cur = ref.current
    setBusy(true)
    try {
      const res = await cur.execute('match_lines', {
        form_no: cur.formNo, header_values: cur.headerValues, code: p.code, qty: Math.abs(p.qty) || 1,
        details: { [cur.formNo]: cur.rows.filter(r => !r._deleted) },
        ...(cur.txnId ? { txn_id: cur.txnId } : {}),
        ...(cur.parentPk && Object.keys(cur.parentPk).length ? { parent_pk: cur.parentPk } : {}),
      }) as {
        rows: LineRowResult[]; match_keys: string[]; quantity_column: string | null; existing: string
      }
      const hit = res.rows?.[0]
      if (!hit) {
        push({ kind: 'missing', code: p.code, qty: p.qty, title: p.code, detail: 'No item has this code' })
        return
      }
      const name = String(Object.values(hit.values).find(v => typeof v === 'string' && v !== p.code) ?? '')
      const qcol = res.quantity_column
      const keys = res.match_keys || []
      const existing = res.existing !== 'new_line' && qcol && keys.length
        ? cur.rows.find(r => !r._deleted && keys.every(k => String(r[k] ?? '').trim().toLowerCase()
          === String(hit.values[k] ?? '').trim().toLowerCase()))
        : undefined
      if (existing && qcol) {
        const rk = cur.rowKeyOf(existing)
        const before = Number(existing[qcol] || 0)
        const after = before + p.qty
        cur.onSetCell(rk, qcol, after)
        const v = evaluateLineLimits({ ...existing, [qcol]: after },
          existing._limits as Record<string, ResolvedLineLimit> | undefined, cur.labelOf)
        const note = v.status?.index === 2 && v.status.delta != null
          ? ` · ${v.status.delta.toLocaleString()} over`
          : v.status?.index === 1 ? ' · in full' : ''
        push({ kind: 'added', code: p.code, qty: p.qty, title: `${p.qty} × ${p.code} ${name}`,
          detail: `line ${lineNo(rk) ?? '?'} · ${before.toLocaleString()} → ${after.toLocaleString()}${note}`,
          undo: { kind: 'set', rowKey: rk, col: qcol, value: existing[qcol] } })
        return
      }
      if (p.qty < 0) {
        push({ kind: 'error', code: p.code, qty: p.qty, title: p.code, detail: 'Not on a line — nothing to take off' })
        return
      }
      const [rk] = cur.onAppend([toBufferRow(hit)])
      push({ kind: 'new', code: p.code, qty: p.qty, title: `${p.qty} × ${p.code} ${name}`,
        detail: `new line ${(cur.rows.filter(r => !r._deleted).length + 1).toLocaleString()}`,
        undo: rk ? { kind: 'remove', rowKey: rk } : undefined })
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string }; detail?: string } }; message?: string })
      push({ kind: 'error', code: p.code, qty: p.qty, title: p.code,
        detail: msg?.response?.data?.error?.message || msg?.response?.data?.detail || msg?.message || 'Could not add' })
    } finally {
      setBusy(false)
    }
  }, [])

  const undoLast = useCallback(() => {
    const last = log.find(e => e.undo)
    if (!last?.undo) return
    if (last.undo.kind === 'set') ref.current.onSetCell(last.undo.rowKey, last.undo.col, last.undo.value)
    else ref.current.onRemoveRow(last.undo.rowKey)
    setLog(prev => prev.map(e => (e.id === last.id ? { ...e, undo: undefined, detail: `${e.detail} · undone` } : e)))
  }, [log])

  return { scan, busy, log, undoLast }
}

export type QuickAddController = ReturnType<typeof useQuickAdd>

/** The scan field (F6 focuses it — bound by the host grid). */
export const QuickAddInput = forwardRef<HTMLInputElement, {
  qa: QuickAddController
  suggestions: (code: string) => Array<{ key: string; code: string; name: string; preview: string }>
  width?: number
}>(function QuickAddInput({ qa, suggestions, width = 420 }, ref) {
  const [text, setText] = useState('')
  const [focus, setFocus] = useState(false)
  const [active, setActive] = useState(0)
  const parsed = parseScan(text)
  const list = useMemo(() => (parsed ? suggestions(parsed.code).slice(0, 5) : []), [parsed?.code, suggestions])
  const submit = (value: string) => {
    if (!value.trim()) return
    void qa.scan(value)
    setText('')
    setActive(0)
  }
  return (
    <div style={{ position: 'relative' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, boxSizing: 'border-box',
        border: `${focus ? 2 : 1}px solid ${focus ? T.accent : '#c9d2dc'}`, borderRadius: 8, padding: '0 10px',
        width, maxWidth: '100%', boxShadow: focus ? '0 0 0 3px rgba(0,97,208,.12)' : undefined, background: T.bg }}>
        <ScanLine size={18} color={focus ? '#0061D0' : '#5b6675'} aria-hidden="true" />
        <input
          ref={ref} value={text} aria-label="Quick add" placeholder="Scan or type an item code — 12*ITM-014 adds 12"
          onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)}
          onChange={e => { setText(e.target.value); setActive(0) }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown' && list.length) { e.preventDefault(); setActive(a => Math.min(a + 1, list.length - 1)) }
            if (e.key === 'ArrowUp' && list.length) { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
            if (e.key === 'Enter') {
              e.preventDefault()
              const pick = list[active]
              submit(pick && parsed && pick.code.toLowerCase().startsWith(parsed.code.toLowerCase()) && pick.code.toLowerCase() !== parsed.code.toLowerCase()
                ? `${parsed.qty}*${pick.code}` : text)
            }
            if (e.key === 'Escape') setText('')
          }}
          style={{ border: 0, outline: 0, flex: 1, minWidth: 0, fontFamily: T.mono, fontSize: 15, fontWeight: 500,
            background: 'transparent', color: T.text }}
        />
        <span style={kbd}>{focus ? 'Enter' : 'F6'}</span>
      </label>
      {focus && parsed && list.length > 0 && (
        <div role="listbox" aria-label="Matching lines" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          width, maxWidth: '86vw', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, zIndex: 30,
          boxShadow: '0 12px 32px rgba(20,28,40,.18)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', fontSize: 13, color: T.muted, background: '#f0f3f7' }}>
            Adds <b style={{ color: T.text }}>{parsed.qty}</b> to · lines on this record first · Enter looks the code up
          </div>
          {list.map((s, i) => (
            <div key={s.key} role="option" aria-selected={i === active}
              onMouseDown={e => { e.preventDefault(); submit(`${parsed.qty}*${s.code}`) }}
              style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 14px', fontSize: 14, cursor: 'pointer',
                background: i === active ? '#e8f0fe' : undefined }}>
              <span style={{ fontFamily: T.mono }}>{s.code}</span> {s.name}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 13, color: i === active ? '#0b3f86' : T.muted }}>{s.preview}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

function ago(at: number): string {
  const s = Math.round((Date.now() - at) / 1000)
  if (s < 45) return 'now'
  const m = Math.round(s / 60)
  return m < 60 ? `${m} min` : `${Math.round(m / 60)} h`
}

/** The session's scans, newest first, with "Undo last". */
export function ScanLog({ qa, onClose }: { qa: QuickAddController; onClose: () => void }) {
  return (
    <aside aria-label="Scan log" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: 340,
      borderLeft: `1px solid ${T.border}`, background: T.bg, flexShrink: 0 }}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center',
        gap: 8, background: '#f0f3f7' }}>
        <b style={{ fontSize: 14 }}>This session</b>
        <span style={{ fontSize: 13, color: T.muted }}>{qa.log.length} scans</span>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={qa.undoLast} disabled={!qa.log.some(e => e.undo)} style={btnSm}>Undo last</button>
        <button type="button" onClick={onClose} aria-label="Hide the scan log" style={{ ...btnSm, padding: '0 6px' }}><X size={13} /></button>
      </div>
      <div style={{ overflow: 'auto', flex: 1 }}>
        {qa.log.map(e => (
          <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: 10, alignItems: 'center',
            padding: '9px 14px', borderBottom: '1px solid #edf0f4', fontSize: 14,
            background: e.kind === 'missing' || e.kind === 'error' ? '#fffafa' : undefined }}>
            {e.kind === 'added' && <Check size={18} color="#136c3a" aria-label="Added" />}
            {e.kind === 'new' && <Plus size={18} color="#5b3cc4" aria-label="New line" />}
            {(e.kind === 'missing' || e.kind === 'error') && <X size={18} color="#a61b1b" aria-label="Not added" />}
            <span>
              <span style={{ fontFamily: T.mono }}>{e.title}</span><br />
              <span style={{ fontSize: 13, color: e.kind === 'missing' || e.kind === 'error' ? '#a61b1b' : T.muted }}>{e.detail}</span>
            </span>
            <span style={{ fontSize: 13, color: T.muted }}>{ago(e.at)}</span>
          </div>
        ))}
        {qa.log.length === 0 && (
          <div style={{ padding: 18, fontSize: 13, color: T.muted }}>Scans appear here as you add them.</div>
        )}
      </div>
    </aside>
  )
}
